// Login token and user, kept on the device between app starts.
import type { User } from '@/types/api';

const TOKEN_KEY = 'dentlog.token';
const USER_KEY = 'dentlog.user';
/** The platform administrator's own session, kept while they work in a clinic (support mode). */
const PLATFORM_KEY = 'dentlog.platform';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage blocked (private mode): the session lasts until reload.
  }
}

let memoryToken: string | null = read(TOKEN_KEY);

export const session = {
  token: () => memoryToken,
  user(): User | null {
    const raw = read(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  },
  save(token: string, user: User) {
    memoryToken = token;
    write(TOKEN_KEY, token);
    write(USER_KEY, JSON.stringify(user));
  },
  saveUser(user: User) {
    write(USER_KEY, JSON.stringify(user));
  },
  /** Support mode: work with the clinic's token, keeping the console's to come back to. */
  enterSupport(token: string, user: User) {
    const platform = { token: memoryToken, user: read(USER_KEY) };
    write(PLATFORM_KEY, JSON.stringify(platform));
    session.save(token, user);
  },
  /** Back to the console: its session, or null when there is none (sign in again). */
  exitSupport(): User | null {
    const raw = read(PLATFORM_KEY);
    write(PLATFORM_KEY, null);
    try {
      const platform = raw
        ? (JSON.parse(raw) as { token: string | null; user: string | null })
        : null;
      if (platform?.token && platform.user) {
        const user = JSON.parse(platform.user) as User;
        session.save(platform.token, user);
        return user;
      }
    } catch {
      // unreadable: sign in again
    }
    session.clear();
    return null;
  },
  clear() {
    memoryToken = null;
    write(TOKEN_KEY, null);
    write(USER_KEY, null);
    write(PLATFORM_KEY, null);
  },
};
